/**
 * @fileoverview Provides utility functions for colorful console logging and terminal UI elements. Includes functions for printing colored messages, managing animations, and streaming content. Offers debug, error, and informational logging with customizable formatting.
 */

export const Red = '\x1b[31m'
export const Green = '\x1b[32m'
export const Yellow = '\x1b[33m'
export const Blue = '\x1b[94m'
export const Magenta = '\x1b[35m'
export const Cyan = '\x1b[36m'
export const White = '\x1b[37m'
export const Gray = '\x1b[90m'
export const Reset = '\x1b[0m'

export function startThinkingAnimation(message: string = 'Thinking...'): () => void {
  const frames = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏']
  let i = 0
  // TODO (mjd): There seems to be some delay in starting the interval so we write the first frame immediately.
  // This shows up when we're building context which is a local operation so it's likely that promise
  // is running.
  process.stdout.write(`\r${Cyan}  ${message}${Reset}`)
  const intervalId = setInterval(() => {
    process.stdout.write(`\r${Cyan}${frames[i]} ${message}${Reset}`)
    i = (i + 1) % frames.length
  }, 100)

  return () => {
    clearInterval(intervalId)
    process.stdout.write('\r\x1b[K') // Clear the line
  }
}

export function reportProgress(message: string, initial: number, total: number): { update: (num: number) => void, finish: () => void } {
  let lastPercent = -1
  const update = (num: number) => {
    const percent = Math.floor((num / total) * 100)
    if (percent !== lastPercent) {
      process.stdout.write(`\r${message} ${percent}% (${num}/${total})`)
      lastPercent = percent
    }
  }
  update(initial)
  return {
    update,
    finish: () => {
      process.stdout.write('\r\x1b[K') // Clear the line
    }
  }
}

export function print_ai_stream(chunk: string) {
  process.stdout.write(`${Green}${chunk}${Reset}`)
}

export function print_ai_stream_code(chunk: string) {
  process.stdout.write(`${Blue}${chunk}${Reset}`)
}

function logWithColor(color: string, logFunction: Function, ...args: any[]) {
  logFunction(color + args[0], ...args.slice(1), Reset)
}

export function print_ai(...args: any[]) {
  logWithColor(Green, console.log, ...args)
}

export function print_user(...args: any[]) {
  logWithColor(Cyan, console.log, ...args)
}

export function info(...args: any[]) {
  console.log(...args)
}

export function debug(...args: any[]) {
  if (isVerbose()) {
    logWithColor(Yellow, console.debug, ...args)
  }
}

function logBlock(
  title: string,
  block: string | object,
  logFn: (...args: any[]) => void,
  baseColor?: string,
) {
  const blockString = typeof block === 'string' ? block : JSON.stringify(block, null, 2)
  const totalWidth = Math.max(36, title.length + 10)
  const titleLength = title.length
  const equalsCount = totalWidth - titleLength - 2
  const leftEqualsCount = Math.floor(equalsCount / 2)
  const rightEqualsCount = equalsCount - leftEqualsCount

  logFn(
    `${baseColor || White}${'='.repeat(leftEqualsCount)} ${title} ${'='.repeat(
      rightEqualsCount,
    )}${Reset}\n${blockString}\n${baseColor || White}${'='.repeat(totalWidth)}${Reset}`,
  )
}

export function debug_block(title: string, block: string | object) {
  logBlock(title, block, debug)
}
export function print_ai_block(title: string, block: string | object) {
  logBlock(title, block, print_ai, Green)
}
export function error_block(title: string, block: string | object) {
  logBlock(title, block, error)
}

export function info_block(title: string, block: string | object) {
  logBlock(title, block, info)
}

export function diff_block(title: string, block: string, baseColor?: string) {
  const coloredBlock = block
    .split('\n')
    .map((line) => {
      if (line.startsWith('+')) {
        return `${Green}${line}${Reset}`
      } else if (line.startsWith('-')) {
        return `${Red}${line}${Reset}`
      }
      if (baseColor) {
        return `${baseColor}${line}${Reset}`
      }
      return line
    })
    .join('\n')
  logBlock(title, coloredBlock, info, baseColor)
}

export function error(...args: any[]) {
  logWithColor(Red, console.error, ...args)
}

export function warn(...args: any[]) {
  logWithColor(Yellow, console.warn, ...args)
}

export function exit_with_error(...args: any[]) {
  error('')
  if (args.length === 1 && args[0].stack) {
    error(args[0].stack)
  } else {
    error(...args)
  }
  process.exit(1)
}

export function isVerbose() {
  return process.env.VERBOSE === 'true'
}
